import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from './store';
import { apiRefresh } from './api';

/**
 * Route guard: tenta restaurar sessão via refresh token (cookie httpOnly)
 * no primeiro mount. Enquanto a tentativa roda, mostra um placeholder simples.
 */
export function RequireAuth() {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const bootstrapping = useAuthStore((s) => s.bootstrapping);
  const setSession = useAuthStore((s) => s.setSession);
  const setBootstrapping = useAuthStore((s) => s.setBootstrapping);
  const clear = useAuthStore((s) => s.clear);

  useEffect(() => {
    let cancelled = false;
    // Já temos user + token em memória: pode seguir.
    if (user && accessToken) {
      setBootstrapping(false);
      return;
    }

    (async () => {
      const result = await apiRefresh();
      if (cancelled) return;
      if (result) {
        setSession(result);
      } else {
        clear();
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (bootstrapping) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <svg className="size-5 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
            <path
              d="M4 12a8 8 0 018-8"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              className="opacity-75"
            />
          </svg>
          Restaurando sessão...
        </div>
      </div>
    );
  }

  if (!user || !accessToken) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <Outlet />;
}

/**
 * Inverso: redireciona para /app se o usuário já está autenticado.
 * Usado em /login e /register para evitar que um usuário logado reveja
 * as telas públicas.
 */
export function RedirectIfAuthed({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  if (user && accessToken) {
    return <Navigate to="/app" replace />;
  }
  return <>{children}</>;
}
